import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Plus, Save, Trash2 } from "lucide-react";
import {
  deletePlatformMaintenance,
  fetchPlatformStatus,
  upsertPlatformMaintenance,
} from "@/lib/platform-api";
import PlatformPage from "@/components/platform/PlatformPage";

const SEVERITIES = [
  { value: "scheduled", label: "Scheduled (upcoming)" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
];

const emptyForm = () => ({
  id: "",
  title: "",
  body: "",
  severity: "scheduled",
  startsAt: "",
  endsAt: "",
  showBanner: true,
});

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function fromLocalInput(value) {
  if (!value) return null;
  try {
    return new Date(value).toISOString();
  } catch {
    return null;
  }
}

export default function PlatformStatus() {
  const [loading, setLoading] = useState(true);
  const [notices, setNotices] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await fetchPlatformStatus();
      setNotices(body?.config?.maintenance || []);
    } catch (err) {
      toast.error(err.message || "Failed to load status config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const editNotice = (n) => {
    setForm({
      id: n.id || "",
      title: n.title || "",
      body: n.body || "",
      severity: n.severity || "scheduled",
      startsAt: toLocalInput(n.startsAt),
      endsAt: toLocalInput(n.endsAt),
      showBanner: n.showBanner !== false,
    });
  };

  const saveNotice = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      await upsertPlatformMaintenance({
        id: form.id || undefined,
        title: form.title.trim(),
        body: form.body.trim(),
        severity: form.severity,
        startsAt: fromLocalInput(form.startsAt),
        endsAt: fromLocalInput(form.endsAt),
        showBanner: form.showBanner,
      });
      toast.success("Maintenance notice saved");
      setForm(emptyForm());
      await load();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeNotice = async (id) => {
    if (!window.confirm("Delete this maintenance notice?")) return;
    try {
      await deletePlatformMaintenance(id);
      toast.success("Deleted");
      if (form.id === id) setForm(emptyForm());
      await load();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  return (
    <PlatformPage
      title="System status"
      description="Configure maintenance windows. Banners appear on the marketing homepage and customer admin console — not on attendee event pages."
      maxWidth="max-w-4xl"
      headerExtra={
        <a
          href="/status"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary text-sm inline-flex items-center gap-2 shrink-0"
        >
          View public page <ExternalLink size={14} />
        </a>
      }
    >
      <div className="surface p-6 space-y-4 w-full">
        <h2 className="text-sm font-medium">{form.id ? "Edit notice" : "New maintenance notice"}</h2>
        <label className="block">
          <span className="text-xs text-[var(--text-tertiary)]">Title</span>
          <input
            className="input-dark w-full mt-1 text-sm"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Scheduled database maintenance"
          />
        </label>
        <label className="block">
          <span className="text-xs text-[var(--text-tertiary)]">Body</span>
          <textarea
            className="input-dark w-full mt-1 min-h-[80px] text-sm"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="We expect reduced performance between…"
          />
        </label>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs text-[var(--text-tertiary)]">Severity</span>
            <select
              className="input-dark w-full mt-1 text-sm"
              value={form.severity}
              onChange={(e) => setForm({ ...form, severity: e.target.value })}
            >
              {SEVERITIES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 mt-6 md:mt-0">
            <input
              type="checkbox"
              checked={form.showBanner}
              onChange={(e) => setForm({ ...form, showBanner: e.target.checked })}
            />
            <span className="text-sm">Show banner on home + admin</span>
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs text-[var(--text-tertiary)]">Starts (local)</span>
            <input
              type="datetime-local"
              className="input-dark w-full mt-1 text-sm"
              value={form.startsAt}
              onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="text-xs text-[var(--text-tertiary)]">Ends (local)</span>
            <input
              type="datetime-local"
              className="input-dark w-full mt-1 text-sm"
              value={form.endsAt}
              onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <button type="button" className="btn-primary text-sm inline-flex items-center gap-2" onClick={saveNotice} disabled={saving}>
            <Save size={14} />
            {saving ? "Saving…" : "Save notice"}
          </button>
          {form.id ? (
            <button type="button" className="btn-ghost text-sm" onClick={() => setForm(emptyForm())}>
              Cancel edit
            </button>
          ) : null}
        </div>
      </div>

      <div className="w-full">
        <h2 className="text-sm font-medium mb-4">Configured notices</h2>
        {loading ? (
          <p className="text-sm text-[var(--text-tertiary)]">Loading…</p>
        ) : notices.length === 0 ? (
          <p className="text-sm text-[var(--text-tertiary)]">No maintenance notices yet.</p>
        ) : (
          <ul className="space-y-3">
            {notices.map((n) => (
              <li
                key={n.id}
                className="surface p-4 flex flex-wrap items-start justify-between gap-3"
              >
                <div>
                  <div className="font-medium">{n.title}</div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)] mt-1">
                    {n.severity}
                    {n.showBanner ? " · banner on" : " · banner off"}
                  </div>
                  {n.body ? <p className="text-sm text-[var(--text-secondary)] mt-2">{n.body}</p> : null}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button type="button" className="btn-ghost text-xs" onClick={() => editNotice(n)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn-ghost text-xs text-[#ef4444]"
                    onClick={() => removeNotice(n.id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          className="btn-secondary text-sm mt-6 inline-flex items-center gap-2"
          onClick={() => setForm(emptyForm())}
        >
          <Plus size={14} />
          New notice
        </button>
      </div>
    </PlatformPage>
  );
}
